import { getUnexpiredToken, useActiveProject } from "@/utils/utils";
import { useCallback, useEffect, useMemo, useState } from "react";

// Import component types
import { AppData, ConnectionData } from "./types";

// Import sub-components
import useSupabaseConnectStore from "@/store/zustand/supabaseConnectStore";
import { useSession } from "@clerk/clerk-react";
import axios from "axios";
import BrowseApps from "./components/BrowseApps";
import InstalledApps from "./components/InstalledApps";
import SearchBar from "./components/SearchBar";
import SupabaseCard from "./components/SupabaseCard";
import TabNavigation from "./components/TabNavigation";

const AppStore = () => {
  const [activeTab, setActiveTab] = useState<"browse" | "installed">(
    "installed",
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("All");
  const [apps, setApps] = useState<AppData[]>([]);
  const [installedApps, setInstalledApps] = useState<ConnectionData[]>([]);

  const { session } = useSession();
  const project = useActiveProject();

  // Create a reusable function to fetch connections
  const fetchConnections = useCallback(async () => {
    const token = await getUnexpiredToken(session);
    if (!token || !project?.id) return;
    try {
      const response = await axios.get(
        `${import.meta.env.VITE_BACKEND_BASE_URL}/pica/connections?project_id=${
          project.id
        }`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      const { success, data, error } = response.data;
      if (success) setInstalledApps(data?.rows || []);
      else if (error) console.error("Failed to fetch connections:", error);
    } catch (error) {
      console.error("Failed to fetch connections:", error);
    }
  }, [Boolean(session), project?.id]);

  // Fetch apps and connections in a single effect
  useEffect(() => {
    const fetchApps = async () => {
      const token = await getUnexpiredToken(session);
      if (!token || !project?.id) return;
      try {
        const appsResponse = await axios.get(
          `${import.meta.env.VITE_BACKEND_BASE_URL}/pica/connectors`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          },
        );
        const appsData = appsResponse.data;
        if (appsData.success) {
          setApps(appsData.data?.rows || []);
          // Only fetch connections if connectors fetch was successful
          await fetchConnections();
        } else if (appsData.error) {
          console.error("Failed to fetch apps:", appsData.error);
        }
      } catch (error) {
        console.error("Failed to fetch apps:", error);
      }
    };
    fetchApps();
  }, [project?.id, fetchConnections, Boolean(session)]);

  // Specify featured apps by key (or name)
  const FEATURED_APP_KEYS = [
    "firecrawl",
    "stripe",
    "openai",
    // add more if you want
  ];

  // Get unique categories from apps (plus 'Featured')
  const appCategories = useMemo(() => {
    if (!apps?.length) return [];
    const sortedApps = [...apps].sort((a, b) => a.name.localeCompare(b.name));
    const featuredApps = sortedApps.filter((app) =>
      FEATURED_APP_KEYS.includes(app.platform || ""),
    );
    const categories = Array.from(
      new Set(sortedApps.map((app) => app.category || "Other")),
    );
    const categoryObjs = categories.map((category) => ({
      name: category,
      apps: sortedApps.filter((app) => (app.category || "Other") === category),
    }));
    return featuredApps.length > 0
      ? [{ name: "Featured", apps: featuredApps }, ...categoryObjs]
      : categoryObjs;
  }, [apps]);

  // Extract available categories for the dropdown (always include 'Featured' if present)
  const availableCategories = useMemo(
    () => appCategories.map((category) => category.name),
    [appCategories],
  );

  // Filter apps based on search term and selected category
  const filteredCategories = useMemo(() => {
    const allApps = [...apps].sort((a, b) => a.name.localeCompare(b.name));
    const searchFiltered = !searchTerm
      ? allApps
      : allApps.filter(
          (app) =>
            app.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            app.description.toLowerCase().includes(searchTerm.toLowerCase()),
        );
    if (selectedCategory === "All") {
      return [{ name: "All Apps", apps: searchFiltered }];
    } else if (selectedCategory === "Featured") {
      return [
        {
          name: "Featured",
          apps: searchFiltered.filter((app) =>
            FEATURED_APP_KEYS.includes(app.platform || ""),
          ),
        },
      ];
    } else {
      const filteredApps = searchFiltered.filter(
        (app) => (app.category || "Other") === selectedCategory,
      );
      return [{ name: selectedCategory, apps: filteredApps }];
    }
  }, [apps, searchTerm, selectedCategory, FEATURED_APP_KEYS]);

  // Get app info for installed connection
  const getAppInfoForConnection = useCallback(
    (connection: ConnectionData) => {
      const platformName = connection.platform;
      if (!platformName) return null;

      const matchingApp = apps.find(
        (app) =>
          app.name.toLowerCase() === platformName.toLowerCase() ||
          app.key.toLowerCase().includes(platformName.toLowerCase()),
      );

      return matchingApp || null;
    },
    [apps],
  );

  const { connectedProjectId } = useSupabaseConnectStore.getState();
  const isSupabaseConnected = !!connectedProjectId;

  // Main component render
  return (
    <div className="h-full text-white overflow-y-auto">
      {/* Tabs */}

      {isSupabaseConnected ? (
        <>
          <TabNavigation
            installedApps={installedApps}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
          />
          {/* Search */}
          {activeTab === "browse" && (
            <SearchBar
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
              availableCategories={availableCategories}
            />
          )}

          <div className="px-4 border-y border-secondary py-4">
            <p className="text-sm text-[#8E8E8E]">
              Connect your app to third-party services like payments, messaging
              and more.{" "}
              <span className="text-[#6448EF] font-medium cursor-pointer hover:underline">
                Learn more
              </span>
            </p>
          </div>

          {/* App Content */}
          {activeTab === "browse" ? (
            <BrowseApps
              categories={filteredCategories}
              installedApps={installedApps}
              refetchConnections={fetchConnections}
              featuredAppKeys={FEATURED_APP_KEYS}
            />
          ) : (
            <InstalledApps
              installedApps={installedApps}
              getAppInfoForConnection={getAppInfoForConnection}
              setActiveTab={setActiveTab}
              refetchConnections={fetchConnections}
            />
          )}
        </>
      ) : (
        <div className="flex flex-col items-center justify-center h-full w-full">
          <SupabaseCard />
        </div>
      )}
    </div>
  );
};

export default AppStore;
